<template>
  <DataTable
    v-if="items.length > 0"
    :loading="loading"
    @close="setFeatureGrid({features: null, featureType: null}); viewMode = 'view'"
  >
    <template #topCenter>
      <TabToggle
        v-model="featureFilter"
        :options="featureFilterOptions"
        :disabled="loading"
        scrollable
        class="mr-2"
      />
    </template>
    <template #topRight>
      <SaveStatus
        v-if="isSaving || lastSaved"
        class="mr-3"
        :saved="isSaving ? undefined : (lastSaved ?? undefined)"
      />
      <TabToggle
        v-model="viewMode"
        :options="viewModeOptions"
        :disabled="loading"
        class="mr-2"
      />
    </template>
    <template #dataView>
      <TableView
        :items="items"
        :columns="columns"
        :get-row-id="getRowId"
        @cell-edit-request="handleCellEditRequest"
      />
    </template>
  </DataTable>
</template>

<script lang="ts">
import {mapState, mapMutations} from 'vuex';
import DataTable from './DataTable.vue';
import TableView from './TableView.vue';
import SaveStatus from '../Reusable/SaveStatus.vue';
import TabToggle from '../Reusable/TabToggle.vue';
import type {ColDef, GridItem, GetRowIdParams, EditableCallbackParams, ValueGetterParams, CellEditRequestEvent} from './types';
import type {Feature} from '@/services/OverlaysService';
import {DisplayField} from '@/lib/classes/InfoWindowContent/DisplayField';
import {Comp} from '@/lib/classes/InfoWindowContent/InfoWindowContent';
import {mapActions} from 'vuex';
import {wrapError} from '@/lib/errors';
import {getCompPresentation, getPresentation} from '@/services/ContentPresentation';
import {deleteDisplayFieldInDatabase} from './columnDeletion';

const FEATURE_FILTER_TAB_ORDER = [
  'placeMarker',
  'Lease',
  'Building Sale',
  'Land Sale',
  'Capital',
];

export default {
  name: 'FeatureGrid',
  components: {
    DataTable,
    TableView,
    SaveStatus,
    TabToggle,
  },
  data() {
    return {
      loading: false,
      isSaving: false,
      lastSaved: null as Date | null,
      featureFilter: '',
      featureFilterOptions: [] as Array<{value: string; label: string}>,
      viewMode: 'view' as 'view' | 'edit',
      viewModeOptions: [
        {value: 'view', label: 'View Mode'},
        {value: 'edit', label: 'Edit Mode'},
      ],
      columns: [] as ColDef[],
      items: [] as GridItem[],
    };
  },
  computed: {
    ...mapState({
      featuresForGrid: (state: any): Map<string, Feature> => state.featuresForGrid,
      featureGridFilter: (state: any): string => state.featureGridFilter,
      dataTitle: (state: any): string => state.dataTitle,
    }),
  },
  watch: {
    featuresForGrid: {
      handler() {
        this.calculateFeatureFilterOptions();
        this.calculateGridData();
      },
      immediate: true,
    },
    featureGridFilter: {
      handler(newFilter) {
        // HACK: BMAPS-2857 - Map individual block plan types to their parent category
        let mappedFilter = newFilter;
        if (newFilter === 'blockPlan-crossDock' || newFilter === 'blockPlan-singleLoad') {
          mappedFilter = 'blockPlan-industrial';
        }

        if (mappedFilter && this.featureFilterOptions.some((option) => option.value === mappedFilter)) {
          this.featureFilter = mappedFilter;
        }
      },
      immediate: true,
    },
    featureFilter() {
      this.calculateGridData();
    },
    viewMode() {
      // Force column header component params to update, otherwise DeletableColumnHeader never sees changes to viewMode
      this.columns = this.calculateColumns();
    },
  },
  mounted() {
    this.calculateFeatureFilterOptions();
    this.calculateGridData();
  },
  methods: {
    ...mapMutations(['setFeatureGrid']),
    ...mapActions(['addErrorMessage', 'addMessage']),
    getRowId(params: GetRowIdParams<GridItem>) {
      // returns "undefined-featureId" for features and "featureId-compId" for comps. As long as it's unique, it
      // shouldn't matter.
      return `${params.data.parent?.id}-${params.data.data.id}`;
    },
    async handleCellEditRequest(event: CellEditRequestEvent<GridItem>) {
      const {newValue, oldValue, data: gridItem, colDef, api} = event;
      const columnName = colDef.headerName;

      if (!gridItem || !columnName) {
        console.error('Invalid cell edit request, missing grid item or column name', event);
        return;
      }

      if (newValue === oldValue) return;


      // To make grid edits as fast as possible and prevent flicker/losing cell focus on grid updates,
      // data updates have been split into two parts: updating the grid data in memory immediately and then saving to
      // the database in the background. The memory update is an optimistic update and may result in a mismatch
      // if the database write fails. This should only happen in case of network issues and should cause immediate
      // feedback to the user via an error message. The mismatch will then be resolved the next time the grid is
      // refreshed, even without a page reload.
      this.isSaving = true;
      let saved = false;
      try {
        if (columnName === 'Feature Name') {
          this.updateFeatureNameInMemory(gridItem, newValue);
        } else if (columnName === 'Marker Name') {
          this.updateMarkerNameInMemory(gridItem, newValue);
        } else {
          this.updateDisplayFieldInMemory(gridItem, columnName, newValue);
        }

        // Immediately refresh the grid data to show the new value. As long as this is performed after the memory
        // update and without `force: true`, it should not cause flicker or loss of cell focus.
        api.refreshCells({columns: [columnName], rowNodes: [event.node]});
        // Perform database updates. We await these to ensure data consistency and prevent race conditions in tests,
        // but the UI already updated optimistically above, so responsiveness is maintained. The returned boolean
        // reflects whether the server confirmed the write, which gates the "Saved" timestamp below.
        if (columnName === 'Feature Name') {
          saved = await this.saveFeatureNameToDatabase(gridItem, newValue);
        } else if (columnName === 'Marker Name') {
          saved = await this.saveMarkerNameToDatabase(gridItem, newValue);
        } else {
          saved = await this.saveDisplayFieldToDatabase(gridItem, columnName, newValue);
        }
      } catch (error) {
        console.error('Error handling cell edit request:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        this.addErrorMessage(`Failed to update cell: ${errorMessage}`);
      } finally {
        this.isSaving = false;
      }

      // Only stamp the timestamp once the server has confirmed the write succeeded.
      if (saved) {
        this.lastSaved = new Date();
      }
    },
    updateFeatureNameInMemory(gridItem: GridItem, newValue: string) {
      const trimmedName = newValue.trim();
      gridItem.data.name = trimmedName;
    },
    updateMarkerNameInMemory(gridItem: GridItem, newValue: string) {
      const trimmedName = newValue.trim();
      if (gridItem.type === 'comp' && gridItem.parent) {
        // For comps, marker name refers to the parent feature name
        gridItem.parent.name = trimmedName;
      }
    },
    updateDisplayFieldInMemory(gridItem: GridItem, columnName: string, newValue: string) {
      const trimmedValue = newValue.trim();
      const displayFields = gridItem.data.displayFields || [];

      // Check for duplicate display field labels (show error and prevent edit)
      const duplicateFields = displayFields.filter((df: any) => df.label === columnName);
      if (duplicateFields.length > 1) {
        console.error(`Multiple display fields found with label "${columnName}". Cannot edit.`);
        this.addErrorMessage(`Cannot edit field "${columnName}": multiple fields with the same name exist. Please remove or rename duplicates first via the InfoWindow editor.`);
        return;
      }

      // If the trimmed value is empty, we treat this as a deletion. The entire display field should be removed, not
      // updated to an empty value.
      const existingDisplayFieldIndex = displayFields.findIndex((df: any) => df.label === columnName);
      if (existingDisplayFieldIndex !== -1) {
        if (trimmedValue === '') {
          displayFields.splice(existingDisplayFieldIndex, 1);
          return;
        }
        displayFields[existingDisplayFieldIndex].value = trimmedValue;
      } else {
        if (trimmedValue === '') return;
        const newDisplayField = this.createNewDisplayField(columnName, trimmedValue);
        gridItem.data.displayFields = gridItem.data.displayFields || [];
        gridItem.data.displayFields.push(newDisplayField);
      }
    },
    async saveFeatureNameToDatabase(gridItem: GridItem, newValue: string): Promise<boolean> {
      try {
        const trimmedName = newValue.trim();
        if (gridItem.type === 'feature') {
          const feature = gridItem.data as Feature;
          await feature.update({name: trimmedName});
        } else if (gridItem.type === 'comp' && gridItem.parent) {
          // For comps, we need to update the comp name within the parent feature
          const feature = gridItem.parent;
          const comp = gridItem.data;

          // Find the comp in the parent's comps array and update its name
          const compIndex = feature.comps?.findIndex((c) => c.id === comp.id);
          if (compIndex !== undefined && compIndex >= 0 && feature.comps) {
            feature.comps[compIndex].name = trimmedName;
            await feature.update({comps: feature.comps});
          }
        }
        return true;
      } catch (error) {
        const wrappedError = wrapError(error, `Failed to save feature name "${newValue}" to database`);
        this.addErrorMessage(`Unable to save changes. Please try again, or contact support if the problem persists.`);
        console.error(wrappedError);
        return false;
      }
    },
    async saveMarkerNameToDatabase(gridItem: GridItem, newValue: string): Promise<boolean> {
      try {
        const trimmedName = newValue.trim();
        if (gridItem.type === 'comp' && gridItem.parent) {
          // For comps, marker name refers to the parent feature name
          const feature = gridItem.parent;
          await feature.update({name: trimmedName});
        } else {
          throw new Error('Marker name can only be saved for comps with a parent feature');
        }
        return true;
      } catch (error) {
        const wrappedError = wrapError(error, `Failed to save marker name "${newValue}" to database`);
        this.addErrorMessage(`Unable to save changes. Please try again, or contact support if the problem persists.`);
        console.error(wrappedError);
        return false;
      }
    },
    async saveDisplayFieldToDatabase(gridItem: GridItem, columnName: string, newValue: string): Promise<boolean> {
      try {
        const trimmedValue = newValue.trim();
        const displayFields = gridItem.data.displayFields || [];
        const existingDisplayField = displayFields.find((df: any) => df.label === columnName);

        if (gridItem.type === 'feature') {
          return await this.updateFeatureDisplayField(
            gridItem.data as Feature,
            columnName,
            trimmedValue,
            existingDisplayField,
          );
        } else if (gridItem.type === 'comp' && gridItem.parent) {
          return await this.updateCompDisplayField(
            gridItem.data as Comp, gridItem.parent, columnName, trimmedValue, existingDisplayField,
          );
        }
        return false;
      } catch (error) {
        const wrappedError = wrapError(error, `Failed to save display field "${columnName}" to database`);
        this.addErrorMessage(`Unable to save changes. Please try again, or contact support if the problem persists.`);
        console.error(wrappedError);
        return false;
      }
    },
    createNewDisplayField(columnName: string, newValue: string): DisplayField {
      return new DisplayField({
        label: columnName.trim(),
        unit: 'string',
        value: newValue || '',
        allowEdit: true,
        allowLabelEdit: true,
        allowDelete: true,
      });
    },
    async updateFeatureDisplayField(
      feature: Feature, columnName: string, newValue: string, existingDisplayField?: DisplayField,
    ): Promise<boolean> {
      try {
        if (newValue === '') {
          await feature.deleteDisplayField(columnName);
        } else if (existingDisplayField) {
          await feature.update(newValue, existingDisplayField.uuid);
        } else {
          const newDisplayField = this.createNewDisplayField(columnName, newValue);
          feature.displayFields = feature.displayFields || [];
          feature.displayFields.push(newDisplayField);
          await feature.update({displayFields: feature.displayFields});
        }
        return true;
      } catch (error) {
        const wrappedError = wrapError(error, `Failed to save feature display field "${columnName}" to database`);
        this.addErrorMessage(`Unable to save changes. Please try again, or contact support if the problem persists.`);
        console.error(wrappedError);
        return false;
      }
    },
    async updateCompDisplayField(
      comp: Comp, feature: Feature, columnName: string, newValue: string, existingDisplayField?: any,
    ): Promise<boolean> {
      try {
        if (newValue === '' && existingDisplayField) {
          await feature.deleteDisplayField(comp.id, columnName);
        } else if (existingDisplayField) {
          await feature.update(newValue, comp.id, existingDisplayField.uuid);
        } else if (newValue !== '') {
          const newDisplayField = this.createNewDisplayField(columnName, newValue);
          comp.displayFields = comp.displayFields || [];
          comp.displayFields.push(newDisplayField);
          await feature.update({comps: feature.comps});
        } else {
          // In the case of deleting an existing field, the in-memory update already removed the field, so just persist
          // the current comps array without changes.
          await feature.update({comps: feature.comps});
        }
        return true;
      } catch (error) {
        const wrappedError = wrapError(error, `Failed to save comp display field "${columnName}" to database`);
        this.addErrorMessage(wrappedError.message);
        console.error(wrappedError);
        return false;
      }
    },
    calculateFeatureFilterOptions() {
      if (!this.featuresForGrid || this.featuresForGrid.size === 0) {
        this.featureFilterOptions = [];
        this.featureFilter = '';
        return;
      }

      const featureTypes = new Set<string>();
      const compTypes = new Set<string>();
      const parentCategoriesToAdd = new Set<string>();
      Array.from(this.featuresForGrid.values()).forEach((feature) => {
        if (feature.featureType) {
          featureTypes.add(feature.featureType);
        }
        if (feature.comps && Array.isArray(feature.comps)) {
          feature.comps.forEach((comp) => {
            if (comp.type) {
              compTypes.add(comp.type);
            }
          });
        }
      });

      const getDefaultName = (featureType: string): string =>
        getPresentation(featureType)?.getPluralizedName() ?? featureType;

      this.featureFilterOptions = [];
      compTypes.forEach((compType) => {
        let compPresentation = getCompPresentation(compType);
        this.featureFilterOptions.push({
          value: compPresentation.getType(),
          label: compPresentation.getPluralizedName(),
        });
      });
      featureTypes.forEach((featureType) => {
        // HACK: BMAPS-2857 needed to group ind block plans. Skip child features that are now grouped under parent
        // categories
        const isChildFeature = featureType === 'blockPlan-crossDock' || featureType === 'blockPlan-singleLoad';

        if (!isChildFeature) {
          this.featureFilterOptions.push({
            value: featureType,
            label: getDefaultName(featureType),
          });
        } else {
          parentCategoriesToAdd.add('blockPlan-industrial');
        }
      });

      // HACK: BMAPS-2857 needed to group ind block plans
      parentCategoriesToAdd.forEach((parentType) => {
        this.featureFilterOptions.push({
          value: parentType,
          label: getDefaultName(parentType),
        });
      });

      const tabRank = (value: string): number => {
        const index = FEATURE_FILTER_TAB_ORDER.indexOf(value);
        return index === -1 ? Number.MAX_SAFE_INTEGER : index;
      };
      this.featureFilterOptions.sort((a, b) => tabRank(a.value) - tabRank(b.value));

      // Set the feature grid filter as default if available, otherwise use the first option
      const hasMatchingFilter = this.featureGridFilter &&
        this.featureFilterOptions.some((option) => option.value === this.featureGridFilter);

      if (hasMatchingFilter) {
        this.featureFilter = this.featureGridFilter;
      } else if (!this.featureFilter && this.featureFilterOptions.length > 0) {
        this.featureFilter = this.featureFilterOptions[0].value;
      }
    },
    calculateItems(features: Feature[], filter: string): GridItem[] {
      // If the filter matches one of the features type directly, then it's a feature filter. Otherwise, it's a comp
      // type filter.
      if (features.some((feature) => feature.featureType === filter)) {
        return features
          .filter((feature) => feature.featureType === filter)
          .map((feature): GridItem => ({
            type: 'feature',
            data: feature,
          }));
      }

      // HACK: BMAPS-2857 needed to group ind block plans. Handle parent category filters
      if (filter === 'blockPlan-industrial') {
        return features
          .filter((feature) => feature.featureType === 'blockPlan-crossDock' || feature.featureType === 'blockPlan-singleLoad')
          .map((feature): GridItem => ({
            type: 'feature',
            data: feature,
          }));
      }

      return features
        .filter((feature) => Array.isArray(feature.comps) && feature.comps.length > 0)
        .flatMap((feature) => {
          return feature.comps
            .filter((comp) => comp.type && comp.type === filter)
            .map((comp): GridItem => ({
              type: 'comp',
              data: comp,
              parent: feature,
            }));
        });
    },
    calculateColumns(): ColDef[] {
      if (this.items.length === 0) return [];

      // Collect all unique field labels from displayFields of all items
      const fieldLabels = new Set<string>();
      this.items.forEach((item) => {
        item.data.displayFields?.forEach((field: any) => {
          fieldLabels.add(field.label);
        });
      });

      const displayFieldValueGetter = (params: ValueGetterParams<GridItem>) => {
        if (!params.data) return '';
        const displayField = params.data.data.displayFields?.find(
          (field: any) => field.label === params.colDef.headerName,
        );
        return displayField ? displayField.value : '';
      };

      const displayFieldEditable = (params: EditableCallbackParams<GridItem>) => {
        if (!params.data || this.viewMode === 'view') return false;
        const displayField = params.data.data.displayFields?.find(
          (field: any) => field.label === params.colDef.headerName,
        );
        // Allow editing if display field doesn't exist (so it can be created) or if allowEdit is true
        return !displayField || displayField.allowEdit === true;
      };
      const nameColumnEditable = (params: EditableCallbackParams<GridItem>) => {
        if (!params.data || this.viewMode === 'view') return false;
        return params.data.type === 'feature' || (params.data.type === 'comp' && !!params.data.parent);
      };

      // Helper function to check if ALL display fields in a column can be deleted
      const columnHasDeletableFields = (label: string): boolean => {
        const fieldsWithLabel = this.items
          .map((item) => item.data.displayFields?.find((field: any) => field.label === label))
          .filter((field) => field !== undefined);

        // If no fields exist with this label, don't allow deletion
        if (fieldsWithLabel.length === 0) return false;

        // Only allow deletion if ALL fields with this label have allowDelete=true
        return fieldsWithLabel.every((field: any) => field.allowDelete === true);
      };

      const columns: ColDef[] = Array.from(fieldLabels).map((label) => {
        const hasDeletableFields = columnHasDeletableFields(label);

        const column: ColDef = {
          headerName: label,
          field: label,
          sortable: true,
          resizable: true,
          filter: true,
          valueGetter: displayFieldValueGetter,
          editable: displayFieldEditable,
        };

        // Only use custom header component for display field columns where ALL fields can be deleted
        // Note: We require ALL display fields with this label to have allowDelete=true.
        // This prevents partial deletions in complex scenarios.
        if (hasDeletableFields) {
          column.headerComponent = 'DeletableColumnHeader';
          column.headerComponentParams = {
            allowDelete: this.viewMode === 'edit',
            onDeleteColumn: this.onDeleteColumn,
          };
        }

        return column;
      });

      const hasComps = this.items.some((item) => item.type === 'comp');
      if (hasComps) {
        columns.unshift({
          headerName: 'Marker Name',
          field: 'Marker Name',
          sortable: true,
          filter: true,
          resizable: true,
          valueGetter: (params: ValueGetterParams<GridItem>) => {
            if (!params.data || params.data.type !== 'comp') return '';
            return params.data.parent?.name || 'Unknown Feature';
          },
          editable: nameColumnEditable,
        });
      }

      columns.unshift({
        headerName: 'Feature Name',
        field: 'Feature Name',
        tooltipField: 'Feature Name',
        filter: true,
        sortable: true,
        resizable: true,
        valueGetter: (params: ValueGetterParams<GridItem>) => {
          if (!params.data) return '';
          return params.data.data.name || 'Untitled Feature';
        },
        editable: nameColumnEditable,
      });

      columns.unshift({ // row number column
        valueGetter: (params: any) => params.node.rowIndex + 1,
        // This color matches the header color
        cellStyle: {backgroundColor: '#FAFAFA', textAlign: 'center'},
        width: 50,
        sortable: false,
        filter: false,
        resizable: false,
        pinned: 'left',
        lockPosition: 'left',
      });

      return columns;
    },
    calculateGridData() {
      if (!this.featuresForGrid || this.featuresForGrid.size === 0) {
        this.items = [];
        this.columns = [];
        return;
      }

      const featuresArray = Array.from(this.featuresForGrid.values());
      this.items = this.calculateItems(featuresArray, this.featureFilter);
      this.columns = this.calculateColumns();
    },
    async onDeleteColumn(columnName: string) {
      // Count how many fields will be deleted
      const fieldsToDelete = this.items.reduce((count, item) => {
        const hasField = item.data.displayFields?.some((df: any) => df.label === columnName);
        return hasField ? count + 1 : count;
      }, 0);

      if (fieldsToDelete === 0) {
        this.addErrorMessage(`No fields found for column "${columnName}" to delete.`);
        return;
      }

      // Show confirmation dialog
      this.addMessage({
        header: 'Delete Field',
        messageType: 'danger',
        body: `Are you sure you want to delete the "${columnName}" field?\n\nThis will remove it from ${fieldsToDelete} infowindow${fieldsToDelete > 1 ? 's' : ''} and cannot be undone.`,
        actions: [{
          title: 'Delete',
          callback: () => this.performColumnDeletion(columnName),
          class: 'primary',
        }],
      });
    },
    async performColumnDeletion(columnName: string) {
      this.loading = true;
      try {
        const itemsToDelete = this.items.filter(
          (item) => item.data.displayFields?.some((df: any) => df.label === columnName),
        );

        const results = await Promise.allSettled(
          itemsToDelete.map((item) => deleteDisplayFieldInDatabase(item, columnName, this.featuresForGrid)),
        );

        const failedItems = itemsToDelete.filter((_, index) => results[index].status === 'rejected');

        if (failedItems.length > 0) {
          const maxNamesShown = 5;
          const shownNames = failedItems.slice(0, maxNamesShown).map((item) => this.describeItemForError(item)).join(', ');
          const remaining = failedItems.length - maxNamesShown;
          const names = remaining > 0 ? `${shownNames}, and ${remaining} more` : shownNames;
          this.addErrorMessage(`Failed to delete "${columnName}" from: ${names}. Please try again.`);
          console.error(`Failed to delete "${columnName}" from ${failedItems.length} of ${results.length} fields.`,
            results.filter((result) => result.status === 'rejected'));
        }

        // Refresh grid data after deletion
        this.calculateGridData();
      } catch (error) {
        const wrappedError = wrapError(error, `Failed to delete column "${columnName}"`);
        this.addErrorMessage(wrappedError.message);
        console.error(wrappedError);
      } finally {
        this.loading = false;
      }
    },
    describeItemForError(item: GridItem): string {
      if (item.type === 'comp') {
        const parentName = item.parent?.name ?? 'Unknown feature';
        return `${parentName} › ${item.data.name ?? item.data.id}`;
      }
      return item.data.name ?? item.data.id;
    },
  },
};
</script>

